import { barPath, useMeasure } from './chartUtils';

export interface RankedRow {
  label: string;
  value: number;
  /** Optional per-row colour. Defaults to the sequential mid-step, so a plain
   *  ranking reads as one measure rather than as several categories. */
  color?: string;
  /** Secondary text, shown in the row's tooltip. */
  meta?: string;
}

export interface RankedBarsProps {
  rows: RankedRow[];
  format?: (v: number) => string;
  /** Width reserved for the label column. */
  labelWidth?: number;
  barHeight?: number;
  /** Scale ceiling, when the bars should be comparable to something off-chart. */
  max?: number;
  emptyMessage?: string;
}

/**
 * Horizontal ranked bars — the right form for a category ranking, because the
 * labels are words and words fit horizontally.
 *
 * Every bar carries its value as text at the end of the row, so the chart never
 * depends on the reader estimating a length, and it stays readable in a
 * screenshot or in one colour.
 */
export function RankedBars({
  rows,
  format = (v) => String(Math.round(v)),
  labelWidth = 168,
  barHeight = 20,
  max,
  emptyMessage = 'Nothing to show.',
}: RankedBarsProps) {
  const [ref, width] = useMeasure<HTMLDivElement>();

  if (!rows.length) return <div className="empty">{emptyMessage}</div>;

  const w = Math.max(width, 260);
  const valueWidth = 74;
  const trackW = Math.max(w - labelWidth - valueWidth, 40);
  const ceiling = Math.max(max ?? 0, ...rows.map((r) => Math.abs(r.value)), 1);
  const rowH = barHeight + 12;
  const height = rows.length * rowH;

  return (
    <div ref={ref} className="chart-ranked">
      <svg className="chart" width={w} height={height} role="img" aria-label="Ranked bar chart">
        {rows.map((row, i) => {
          const y = i * rowH + 6;
          const barW = (Math.abs(row.value) / ceiling) * trackW;
          return (
            <g key={`${row.label}-${i}`}>
              <text className="chart-row-label" x={0} y={y + barHeight / 2} dominantBaseline="middle">
                {row.label.length > 26 ? `${row.label.slice(0, 25)}…` : row.label}
              </text>

              {/* The track: the same hue as the bar rather than a grey, so the
                  filled and unfilled halves read as one object. */}
              <rect className="chart-track" x={labelWidth} y={y} width={trackW} height={barHeight} rx={4} />

              <path
                /* Floored at 2px so a nonzero value is never invisible — a row
                   worth listing is a row worth seeing. */
                d={barPath(labelWidth, y, Math.max(barW, 2), barHeight, 'right', 4)}
                style={{ fill: row.color ?? 'var(--seq-450)' }}
              />

              <text
                className="chart-direct-label"
                x={w}
                y={y + barHeight / 2}
                textAnchor="end"
                dominantBaseline="middle"
              >
                {format(row.value)}
              </text>

              <title>{row.meta ? `${row.label} — ${format(row.value)} (${row.meta})` : `${row.label} — ${format(row.value)}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
